/**
 * Authenticated shell: top bar + left nav + outlet.
 *
 * Also acts as the auth gate. If the session resource resolved to
 * "unauthenticated", we send the admin to `/login` before any
 * nested page tries to render. A spinner covers the in-flight
 * probe so first-paint doesn't flash the wrong layout.
 */

import { For, JSX, Show, createEffect, createSignal } from "solid-js";
import { A, RouteSectionProps, useLocation, useNavigate } from "@solidjs/router";

import type { AdminRole } from "../lib/api";
import { SIGN_OUT_FAILED } from "../lib/noctantLogin";
import { roleBadgeClass, roleLabel, useSession } from "../lib/session";
import FirmPicker from "./FirmPicker";
import type { OrgWords } from "@noctant/challenge-builder";
import { firmWords } from "../lib/orgWords";

interface NavItem {
  href: string;
  label: string | ((said: OrgWords | null) => string);
  /** The roles that see the item; every staff role when absent. */
  roles?: readonly AdminRole[];
}

const STAFF: readonly AdminRole[] = [
  "noctant_superadmin",
  "operator_admin",
  "operator_support",
];

// The environment monitor (the deploy's admin smoke) reads the environment
// report and nothing else; the server refuses it every other route.
const NAV: NavItem[] = [
  { href: "/", label: "Dashboard" },
  { href: "/users", label: "Members" },
  { href: "/challenges", label: (said) => said?.area ?? "" },
  { href: "/templates", label: "Templates" },
  { href: "/operators", label: "Operators" },
  { href: "/audit-log", label: "Audit log" },
  {
    href: "/environment",
    label: "Environment",
    roles: ["noctant_superadmin", "environment_monitor"],
  },
  { href: "/firms/new", label: "New firm", roles: ["noctant_superadmin"] },
];

export default function AppShell(props: RouteSectionProps): JSX.Element {
  const session = useSession();
  const words = () => firmWords(session.admin());
  const navigate = useNavigate();
  const location = useLocation();

  // Boot-time redirect to /login if the cookie is missing.
  createEffect(() => {
    if (session.unauthenticated()) {
      navigate("/login", { replace: true });
    }
  });

  const [logoutFailed, setLogoutFailed] = createSignal(false);

  async function onLogout() {
    setLogoutFailed(false);
    try {
      await session.logout();
    } catch (failure) {
      console.warn("The sign-out did not happen.", failure);
      setLogoutFailed(true);
      return;
    }
    navigate("/login", { replace: true });
  }

  return (
    <Show
      when={session.admin()}
      fallback={
        <div class="min-h-screen grid place-items-center text-sm text-slate-500">
          Loading…
        </div>
      }
    >
      {(admin) => (
        <div class="min-h-screen flex flex-col">
          {/* Top bar */}
          <header class="border-b border-slate-200 bg-white">
            <div class="px-4 h-12 flex items-center justify-between">
              <div class="flex items-center gap-3">
                <span class="font-semibold tracking-tight">Noctant admin</span>
              </div>
              <div class="flex items-center gap-3 text-sm">
                <Show when={admin().role === "noctant_superadmin"}>
                  <FirmPicker />
                </Show>
                <div class="flex items-center gap-2">
                  <Show when={admin().display_name}>
                    <span class="text-slate-700">{admin().display_name}</span>
                  </Show>
                  <span class={roleBadgeClass(admin().role)}>
                    {roleLabel(admin().role)}
                  </span>
                </div>
                <button class="btn-secondary" onClick={onLogout}>
                  Log out
                </button>
              </div>
            </div>
            <Show when={logoutFailed()}>
              <p class="px-4 pb-2 text-sm text-red-600" role="alert">{SIGN_OUT_FAILED}</p>
            </Show>
          </header>

          {/* Nav + content */}
          <div class="flex-1 flex">
            <nav class="w-56 border-r border-slate-200 bg-white py-4">
              <ul class="space-y-0.5">
                <For each={visibleNav(admin().role)}>
                  {(item) => (
                    <li>
                      <A
                        href={item.href}
                        end={item.href === "/"}
                        classList={{
                          "block px-4 py-1.5 text-sm hover:bg-slate-100": true,
                          "bg-slate-900 text-white hover:bg-slate-900":
                            location.pathname === item.href ||
                            (item.href !== "/" &&
                              location.pathname.startsWith(item.href)),
                        }}
                      >
                        {typeof item.label === "string" ? item.label : item.label(words())}
                      </A>
                    </li>
                  )}
                </For>
              </ul>
            </nav>
            <main class="flex-1 p-6 bg-slate-50">{props.children}</main>
          </div>
        </div>
      )}
    </Show>
  );
}

function visibleNav(role: AdminRole): NavItem[] {
  return NAV.filter((item) => (item.roles ?? STAFF).includes(role));
}
